import React, { useRef, useState } from "react";
import { Alert, Button, Collapse, IconButton, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useLiveDataStatus } from "../../dds/useLiveDataStatus";

/**
 * Live-data connection state, for the operator.
 *
 * Without something like this a DDS outage is INVISIBLE: subscriptions error to
 * console.error and the UI silently freezes on stale data. The operator sees a
 * clock that stopped and a score that will not move, which is indistinguishable
 * from the app being broken.
 *
 * THREE TIMES THIS COMPONENT HAS BEEN THE PROBLEM RATHER THAN THE WARNING
 * ----------------------------------------------------------------------
 * 1. IT CLIPPED THE PAGE (OPUS-1466). DashboardLayout's content Box is
 *    `height: 100vh` with `overflow: hidden`, and this banner rendered IN FLOW
 *    above <Outlet/>. Every pixel it took came out of a container that does not
 *    scroll, so the bottom of the routed page became unreachable — buttons
 *    included. It now overlays the content, so it cannot change the height
 *    available to the page no matter what it says. THAT IS STILL LOAD-BEARING:
 *    do not move it back into flow.
 *
 * 2. IT WAS ALMOST ALWAYS ON (OPUS-1466). `stale` means "connected, but no
 *    message for 30s". Sensible for AeDrive during a live match; meaningless in
 *    an admin panel, where with no match in progress there is nothing to push
 *    and 30s of silence is simply Tuesday. `stale` no longer raises it.
 *
 * 3. IT LATCHED ON "RECONNECTING" FOREVER, including on pages that never open a
 *    socket. The transport counts graphql-ws's own lazy close as a failure, and
 *    with nothing subscribed nothing ever reports success again. The fix is in
 *    useLiveDataStatus + subscriptionRegistry: nothing is reported unless this
 *    app is actually holding a subscription, and a fault must survive a grace
 *    period first. See those files for the full mechanism.
 *
 * The bar this has to clear: a banner that is on when nothing is wrong is not a
 * warning, it is furniture, and it trains operators to ignore the one case that
 * matters.
 */
export function DDSStatusBanner() {
  const { kind, visible, attempt, canRetry, dismiss, retry } = useLiveDataStatus();
  const [retryCooling, setRetryCooling] = useState(false);

  const offline = kind === "offline";
  const recovered = kind === "recovered";

  const message =
    kind === "offline"
      ? "Live data disconnected. Retrying periodically."
      : kind === "reconnecting"
        ? `Reconnecting to live data${attempt > 1 ? ` (attempt ${attempt})` : ""}…`
        // A first connect is NOT a reconnect. Calling it one told operators
        // something had gone wrong when the app was simply starting up.
        : kind === "connecting"
          ? "Connecting to live data…"
          : recovered
            ? "Live data reconnected."
            : null;

  // Hold the last non-empty message so the Collapse animates out with text in
  // it rather than blanking mid-transition.
  const shown = useRef(message);
  if (message) shown.current = message;

  const handleRetry = () => {
    if (retryCooling) return;
    setRetryCooling(true);
    retry();
    setTimeout(() => setRetryCooling(false), 2000);
  };

  return (
    <Box
      sx={{
        // Overlay, not layout — see note 1 above.
        position: "absolute",
        top: 8,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: (theme) => theme.zIndex.snackbar,
        width: "auto",
        maxWidth: "min(680px, 92%)",
        // Let clicks through when there is nothing to show, so the overlay can
        // never become an invisible click-trap over the page.
        pointerEvents: visible ? "auto" : "none",
      }}
    >
      {/* unmountOnExit: a collapsed Collapse still keeps its children in the
          DOM. A hidden role="alert" left in the accessibility tree is a warning
          a screen reader can still find with nothing wrong — the same class of
          lie this component keeps being fixed for. */}
      <Collapse in={visible} unmountOnExit>
        <Alert
          data-testid="dds-status-banner"
          severity={offline ? "error" : recovered ? "success" : "info"}
          variant="filled"
          // Only a genuine outage earns an interruption. MUI's Alert defaults to
          // role="alert", which cuts across whatever a screen reader is saying.
          role={offline ? "alert" : "status"}
          aria-live={offline ? "assertive" : "polite"}
          sx={{ boxShadow: 3, alignItems: "center" }}
          action={
            // The recovery note is information, not a decision: no buttons, it
            // clears itself.
            recovered ? null : (
              <>
                {canRetry && (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={handleRetry}
                    disabled={retryCooling}
                  >
                    Retry now
                  </Button>
                )}
                <IconButton
                  size="small"
                  color="inherit"
                  aria-label="Dismiss live-data status"
                  onClick={dismiss}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              </>
            )
          }
        >
          {shown.current}
        </Alert>
      </Collapse>
    </Box>
  );
}

export default DDSStatusBanner;
